import {
  ReactElement,
  SyntheticEvent,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  AppBar,
  Stack,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { isNull } from 'lodash';
import { useNavigate } from 'react-router-dom';

import {
  NewFeatureBagde,
  TrackingOrderPageInfo,
  AccessFeature,
  OrderPendingStateChip,
  OrderOnAgendaStateChip,
  OrderFinalizedStateChip,
  OrderRevisitChip,
} from '~components/index';
import { useRequest } from '~hooks/index';
import { getItemById } from '~services/item';
import { ItemCreateExtended, ItemExtended } from '~services/item/types';
import { Nullable } from '~globals/types';
import { RouteItemStateType, PlanFeaturesTypes } from '~globals/types/enums';
import { PATHS } from '~constants/paths';
import { openUrlInNewTab } from '~utils/commons';
import { replaceParamsPath } from '~utils/router';
import {
  isPendingOrder,
  isOnAgendaOrder,
  convertItemCreateExtendedToItemExtended,
} from '~utils/order';

import {
  DialogDetailsOrderProps,
  DialogDetailsOrderActiveTab,
  DialogDetailsOrderTabsList,
  DialogDetailsOrderTabsListPanel,
} from './types';
import DialogDetailsOrderProvider from './DialogDetailsOrderContext';
import {
  DialogDetailsOrderData,
  DialogDetailsOrderArticles,
  DialogDetailsOrderOrders,
  DialogDetailsOrderHistory,
} from './Tabs';
import {
  DialogDetailsOrderContainer,
  DialogDetailsOrderLoadingContainer,
  DialogDetailsOrderHeader,
  DialogDetailsOrderHeaderTitle,
  DialogDetailsOrderContentContainer,
  DialogDetailsOrderTab,
  DialogDetailsOrderTabsPanelContainer,
  DialogDetailsOrderTabPanel,
} from './styles';

export const DialogDetailsOrderBadgeState = ({
  itemData,
}: {
  itemData: ItemExtended;
}): ReactElement => {
  if (isPendingOrder(itemData)) {
    return (
      <OrderPendingStateChip
        itemStateTypeId={itemData.itemStateTypeId}
        collectItemStateTypeId={itemData.collectItemStateTypeId}
        size="small"
      />
    );
  }

  if (isOnAgendaOrder(itemData)) {
    const orderAgendaStateId = itemData.itemStateTypeId as
      | RouteItemStateType.OnAgenda
      | RouteItemStateType.InProgress;

    return <OrderOnAgendaStateChip stateId={orderAgendaStateId} size="small" />;
  }

  const orderFinalizedStateId = itemData.itemStateTypeId as
    | RouteItemStateType.FinalizedSuccess
    | RouteItemStateType.FinalizedError
    | RouteItemStateType.WithoutVisiting;

  return (
    <OrderFinalizedStateChip stateId={orderFinalizedStateId} size="small" />
  );
};

const DialogDetailsOrder = ({
  open,
  onClose,
  orderId,
  enabledChipTitle = true,
  currentTab,
  showInfoInNewWindow = false,
}: DialogDetailsOrderProps): ReactElement => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<DialogDetailsOrderActiveTab>(
    currentTab ?? 'data',
  );

  const [detailsData, setDeatilsData] =
    useState<Nullable<ItemCreateExtended>>(null);
  const [isCollectItem, setIsCollectItem] = useState(false);

  const [, loadingDetails, , reloadRequest] = useRequest({
    request: getItemById,
    payload: orderId,
    withPostSuccess: (response) => {
      const detailsResponse = response.data?.data as ItemCreateExtended;

      setDeatilsData(detailsResponse);
      setIsCollectItem(Boolean(detailsResponse.isCollectItem));
    },
  });

  const reloadData = useCallback(() => {
    reloadRequest(orderId);
  }, [reloadRequest, orderId]);

  const tabsList = useMemo(
    (): DialogDetailsOrderTabsList[] => [
      {
        id: 'data',
        label: 'Datos de parada',
        enabled: true,
        newActiveForVersion: '',
      },
      {
        id: 'articles',
        label: 'Artículos / Tareas',
        enabled: !isCollectItem,
        newActiveForVersion: '1.14.0',
      },
      {
        id: 'orders',
        label: 'Pedidos',
        enabled: isCollectItem,
        newActiveForVersion: '',
      },
      {
        id: 'history',
        label: 'Historial',
        enabled: true,
        newActiveForVersion: '',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
      },
    ],
    [isCollectItem],
  );

  const tabsListPanels = useMemo(
    (): DialogDetailsOrderTabsListPanel[] => [
      {
        id: tabsList[0].id,
        panel: DialogDetailsOrderData,
        enabled: true,
      },
      {
        id: tabsList[1].id,
        panel: DialogDetailsOrderArticles,
        enabled: !isCollectItem,
      },
      {
        id: tabsList[2].id,
        panel: DialogDetailsOrderOrders,
        enabled: isCollectItem,
      },
      {
        id: tabsList[3].id,
        panel: DialogDetailsOrderHistory,
        enabled: true,
      },
    ],
    [tabsList, isCollectItem],
  );

  const handleChangeActiveTab = useCallback(
    (_e: SyntheticEvent, newValue: DialogDetailsOrderActiveTab) => {
      setActiveTab(newValue);
    },
    [],
  );

  const onHandleClickShowInfo = useCallback(() => {
    const urlShowInfoOrder = replaceParamsPath(PATHS.ORDERS.SHOW, {
      ':orderId': orderId,
    });

    if (showInfoInNewWindow) {
      openUrlInNewTab(urlShowInfoOrder);
    } else {
      navigate(urlShowInfoOrder);
    }

    onClose();
  }, [orderId, showInfoInNewWindow, navigate, onClose]);

  return (
    <DialogDetailsOrderProvider
      value={{ data: detailsData, reloadData, onClose, orderId }}
    >
      <DialogDetailsOrderContainer open={open} maxWidth={false}>
        {loadingDetails && (
          <DialogDetailsOrderLoadingContainer>
            <CircularProgress color="primary" disableShrink size={80} />
          </DialogDetailsOrderLoadingContainer>
        )}

        {!loadingDetails && (
          <>
            <DialogDetailsOrderHeader>
              <DialogDetailsOrderHeaderTitle>
                <Typography component="h2" variant="h6">
                  {detailsData?.title}
                </Typography>

                {enabledChipTitle && !isNull(detailsData) && (
                  <Stack direction="row" spacing={1}>
                    <DialogDetailsOrderBadgeState
                      itemData={convertItemCreateExtendedToItemExtended(
                        detailsData,
                      )}
                    />

                    {(detailsData.visitCount ?? 0) > 0 && (
                      <OrderRevisitChip size="small" />
                    )}
                  </Stack>
                )}
              </DialogDetailsOrderHeaderTitle>

              {detailsData?.trackingCode && (
                <TrackingOrderPageInfo
                  trackingCode={detailsData.trackingCode}
                />
              )}
            </DialogDetailsOrderHeader>

            <DialogDetailsOrderContentContainer>
              <TabContext value={activeTab}>
                <AppBar position="sticky" color="tertiary" elevation={0}>
                  <TabList
                    onChange={handleChangeActiveTab}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    {tabsList.map(
                      ({
                        label,
                        id,
                        enabled,
                        newActiveForVersion,
                        validSubscriptionFeature,
                      }) =>
                        enabled && (
                          <DialogDetailsOrderTab
                            key={`dialog-details-order-tab-${id}`}
                            label={label}
                            value={id}
                            icon={
                              <div>
                                {newActiveForVersion && (
                                  <NewFeatureBagde
                                    activeForVersion={newActiveForVersion}
                                  />
                                )}

                                {validSubscriptionFeature && (
                                  <AccessFeature.NextPlanChip
                                    {...validSubscriptionFeature}
                                  />
                                )}
                              </div>
                            }
                            iconPosition="end"
                          />
                        ),
                    )}
                  </TabList>
                </AppBar>

                <DialogDetailsOrderTabsPanelContainer>
                  {tabsListPanels.map(
                    ({ id, panel: Panel, enabled }) =>
                      enabled && (
                        <DialogDetailsOrderTabPanel
                          key={`dialog-details-order-panel-${id}`}
                          value={id}
                        >
                          <Panel />
                        </DialogDetailsOrderTabPanel>
                      ),
                  )}
                </DialogDetailsOrderTabsPanelContainer>
              </TabContext>
            </DialogDetailsOrderContentContainer>

            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button color="secondary" variant="outlined" onClick={onClose}>
                Cerrar
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={onHandleClickShowInfo}
              >
                Ver información completa
              </Button>
            </DialogActions>
          </>
        )}
      </DialogDetailsOrderContainer>
    </DialogDetailsOrderProvider>
  );
};

export default DialogDetailsOrder;
